import { ValidationStatus } from '@policyvision/contracts';
import { ChevronDown, InfoIcon, Plus, Trash } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/common/components/ui/button';
import { Textarea } from '@/common/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/common/components/ui/tooltip';

export interface MissingSectionProps {
  onOpen?: () => void;
  onEditingChange?: (isEditing: boolean) => void;
  onSave?: (description: string) => void;
  onDelete?: () => void;
  validation?: ValidationStatus;
  identifier?: string;
}

export const MissingSection = ({
  onOpen,
  onEditingChange,
  onSave,
  onDelete,
  validation,
  identifier,
}: MissingSectionProps) => {
  const { t } = useTranslation();

  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [isOpening, setIsOpening] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [description, setDescription] = useState<string>('');

  const hasSavedValidation = validation?.status === 'ERROR';
  const savedDescription = hasSavedValidation
    ? validation.description.replace(/^\[PAGES:[^\]]+\]\s*/, '')
    : '';

  const handleAnimationEnd = () => {
    if (isClosing) {
      setIsOpen(false);
      setIsClosing(false);
      setIsDeleting(false);
      onEditingChange?.(false);
    }
    if (isOpening) {
      setIsOpening(false);
    }
  };

  const handleCancel = () => {
    if (!hasSavedValidation) {
      setDescription('');
    }
    setIsClosing(true);
  };

  const handleSave = () => {
    onSave?.(description);
    setIsClosing(true);
  };

  const handleDelete = () => {
    onDelete?.();
    setDescription('');
    setIsDeleting(true);
    setIsClosing(true);
  };

  const handleToggleExpand = () => {
    if (isOpen) {
      setIsClosing(true);
    } else {
      setIsOpening(true);
      setIsOpen(true);
      setDescription(savedDescription);
      onOpen?.();
      onEditingChange?.(true);
    }
  };

  // Saved and collapsed state - show button with chevron
  if (hasSavedValidation && !isOpen) {
    return (
      <div className="py-2">
        <Button
          variant="outline"
          className="w-full justify-center bg-red-4! hover:bg-red-4! transition-opacity duration-200"
          size="sm"
          onClick={handleToggleExpand}
        >
          <span>{t('coverage.missingSection.title')}</span>
          <ChevronDown className="w-4 h-5 opacity-50" />
        </Button>
      </div>
    );
  }

  // Expanded form state (when editing)
  if (isOpen) {
    return (
      <>
        <div
          className={`w-full rounded-lg overflow-hidden ring-2 ring-primary ring-opacity-50 p-2 bg-card ${
            isClosing ? 'section-closing' : 'section-opening'
          }`}
          onAnimationEnd={handleAnimationEnd}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium flex gap-1 items-center">
                {t('coverage.addDescription')}{' '}
                <span className="font-light text-xs">
                  ({t('coverage.required')})
                </span>
              </h3>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center">
                      <InfoIcon className="w-4 h-4 text-muted-foreground stroke-2" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <div className="space-y-2 text-xs font-light">
                      <p>{t('coverage.addDescriptionTooltip.intro')}</p>

                      <ul className="list-disc pl-4 space-y-1">
                        {t('coverage.addDescriptionTooltip.items', {
                          returnObjects: true,
                        }).map((item: string) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>

                      <p className="pt-1">
                        {t('coverage.addDescriptionTooltip.outro')}
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('coverage.descriptionPlaceholder')}
            className="min-h-[150px] mb-4"
          />
          <div className="flex justify-between items-center">
            {hasSavedValidation && !isClosing && (
              <Button
                variant="destructive"
                onClick={handleDelete}
                className="flex items-center gap-1.5"
              >
                <Trash className="w-4 h-4" />
                {t('actions.delete')}
              </Button>
            )}
            <div className="flex gap-4 ml-auto">
              <Button variant="outline" onClick={handleCancel}>
                {t('actions.cancel')}
              </Button>
              <Button
                variant="secondary"
                onClick={handleSave}
                disabled={!description.trim()}
              >
                {t('actions.save')}
              </Button>
            </div>
          </div>
        </div>
        {isClosing && (
          <>
            {isDeleting ? (
              <div className="py-2 animate-fade-in">
                <div className="flex items-center gap-4">
                  <div className="flex-1 border-t-2 border-dashed border-border" />
                  <Button
                    variant="outline"
                    size="sm"
                    disabled
                    className="pointer-events-none"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {t('coverage.missingSection.title')}
                    </span>
                  </Button>
                  <div className="flex-1 border-t-2 border-dashed border-border" />
                </div>
              </div>
            ) : (
              <div className="py-2">
                <Button
                  variant="outline"
                  className="w-full justify-center bg-red-4! hover:bg-red-4! animate-fade-in pointer-events-none"
                  size="sm"
                  disabled
                >
                  <span>{t('coverage.missingSection.title')}</span>
                  <ChevronDown className="w-4 h-5 opacity-50" />
                </Button>
              </div>
            )}
          </>
        )}
      </>
    );
  }

  // Initial state - separator with + button
  return (
    <div
      key={identifier}
      className="flex items-center gap-4 py-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex-1 border-t-2 border-dashed border-border" />
      <Button
        variant="outline"
        size="sm"
        disabled={!isHovered}
        onClick={() => {
          setIsOpen(true);
          onOpen?.();
          onEditingChange?.(true);
        }}
      >
        <Plus className="w-4 h-4" />
        <span className="text-sm font-medium">
          {t('coverage.missingSection.title')}
        </span>
      </Button>
      <div className="flex-1 border-t-2 border-dashed border-border" />
    </div>
  );
};
